import React, { useState, useEffect, useMemo } from 'react';
import { useBars } from '../hooks/useBars';
import type { Bar } from '../hooks/useBars';
import { BarModal } from '../components/BarModal';
import { useAuthContext } from '../contexts/AuthContext';
import { logAction, getCurrentAdmin } from '../services/logger';
import { supabase } from '../lib/supabase';
import { Pencil, Trash2, Search, Star, Plus, Eye, EyeOff, X } from 'lucide-react';
import toast from 'react-hot-toast';

// Normalise une chaîne pour recherche accent-insensible.
// NFD décompose les caractères accentués (é → e + ́), puis on supprime les diacritiques.
// Ainsi "chez leon" matche "Chez Léon" et inversement.
function normalize(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

type ProbabilityFilter = 'all' | 'high' | 'medium' | 'low' | 'none';
type VisibilityFilter = 'all' | 'visible' | 'hidden';
type OnlineFilter = 'all' | 'online' | 'offline';
type ChannelFilter = 'all' | 'Canal+' | 'BeIN' | 'RMC' | 'DAZN' | 'Eurosport';
type RatingFilter = 'all' | 'gte45' | 'gte40' | 'none';
type ScreensFilter = 'all' | 'unknown';

export function BarsPage() {
  const { canDelete } = useAuthContext();
  const { bars, loading, createBar, updateBar, deleteBar, refetch } = useBars();
  const [editBar, setEditBar] = useState<Bar | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');
  const [probability, setProbability] = useState<ProbabilityFilter>('all');
  const [visibility, setVisibility] = useState<VisibilityFilter>('all');
  const [online, setOnline] = useState<OnlineFilter>('all');
  const [channel, setChannel] = useState<ChannelFilter>('all');
  const [rating, setRating] = useState<RatingFilter>('all');
  const [screens, setScreens] = useState<ScreensFilter>('all');

  // Realtime for bars
  useEffect(() => {
    const channel = supabase.channel('bars-admin-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'bars' }, () => { refetch(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [refetch]);

  // Recherche accent-insensible + filtres cumulés
  const filtered = useMemo(() => {
    const q = normalize(search.trim());
    return bars.filter(b => {
      if (q) {
        const name = normalize(b.name || '');
        const addr = normalize(b.address || '');
        if (!name.includes(q) && !addr.includes(q)) return false;
      }
      if (probability !== 'all') {
        const p = b.probability;
        if (probability === 'none' && p != null) return false;
        if (probability === 'high' && (p == null || p < 80)) return false;
        if (probability === 'medium' && (p == null || p < 50 || p >= 80)) return false;
        if (probability === 'low' && (p == null || p >= 50)) return false;
      }
      if (visibility === 'visible' && !b.is_visible) return false;
      if (visibility === 'hidden' && b.is_visible) return false;
      if (online === 'online' && !b.is_online) return false;
      if (online === 'offline' && b.is_online) return false;
      if (channel !== 'all') {
        if (!b.tv_channels || !b.tv_channels.toLowerCase().includes(channel.toLowerCase())) return false;
      }
      if (rating !== 'all') {
        const r = b.google_rating;
        if (rating === 'none' && r != null) return false;
        if (rating === 'gte45' && (r == null || r < 4.5)) return false;
        if (rating === 'gte40' && (r == null || r < 4.0)) return false;
      }
      if (screens === 'unknown' && b.screens_count != null) return false;
      return true;
    });
  }, [bars, search, probability, visibility, online, channel, rating, screens]);

  const activeFilterCount = [
    probability !== 'all',
    visibility !== 'all',
    online !== 'all',
    channel !== 'all',
    rating !== 'all',
    screens !== 'all',
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearch('');
    setProbability('all');
    setVisibility('all');
    setOnline('all');
    setChannel('all');
    setRating('all');
    setScreens('all');
  };

  const handleSave = async (id: string | null, data: Partial<Bar>) => {
    const admin = getCurrentAdmin();
    if (id) {
      // Update existing
      const bar = bars.find(b => b.id === id);
      const oldScreens = bar?.screens_count ?? null;
      const newScreens = data.screens_count ?? null;
      await updateBar(id, data);
      await logAction('update_bar', 'bar', id, { name: bar?.name, address: bar?.address }, data,
        `${admin?.name} a modifié le bar ${data.name}`);
      // Log spécifique si screens_count change (pour audit "complétion data" facile)
      if (oldScreens !== newScreens) {
        const fmt = (v: number | null) => v == null ? 'inconnu' : `${v} écran${v > 1 ? 's' : ''}`;
        await logAction(
          'update_screens_count', 'bar', id,
          { screens_count: oldScreens }, { screens_count: newScreens },
          `${admin?.name} a passé ${bar?.name || data.name} de ${fmt(oldScreens)} à ${fmt(newScreens)}`
        );
      }
      toast.success('Bar mis à jour');
    } else {
      // Create new
      await createBar(data);
      await logAction('add_bar', 'bar', null, null, data,
        `${admin?.name} a ajouté le bar ${data.name}`);
      toast.success('Bar ajouté');
    }
  };

  const handleToggleVisibility = async (bar: Bar) => {
    const admin = getCurrentAdmin();
    const next = !bar.is_visible;
    try {
      await updateBar(bar.id, { is_visible: next });
      await logAction(
        'toggle_visibility',
        'bar',
        bar.id,
        { is_visible: bar.is_visible },
        { is_visible: next },
        `${admin?.name} a ${next ? 'rendu visible' : 'masqué'} le bar ${bar.name}`
      );
      toast.success(next ? `${bar.name} visible dans l'app` : `${bar.name} masqué dans l'app`);
    } catch (err: any) {
      console.error('Toggle visibility error:', err);
      toast.error(err?.message || 'Erreur toggle visibilité');
    }
  };

  const handleDeleteBar = async (bar: Bar) => {
    if (!canDelete) { toast.error('Seuls les super admins peuvent supprimer des bars'); return; }
    if (!window.confirm(`Supprimer le bar "${bar.name}" et toutes ses diffusions ?`)) return;
    const admin = getCurrentAdmin();
    await deleteBar(bar.id);
    await logAction('delete_bar', 'bar', bar.id, { name: bar.name, address: bar.address }, null,
      `${admin?.name} a supprimé le bar ${bar.name}`);
    toast.success('Bar supprimé');
    setEditBar(null);
  };

  const probBadge = (p: number | null) => {
    if (p == null) return <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>-</span>;
    const color = p >= 80 ? 'var(--green)' : p >= 60 ? 'var(--orange)' : 'var(--red)';
    return <span style={{ fontSize: '11px', fontWeight: 600, color }}>{p}%</span>;
  };

  return (
    <div>
      <div className="filters-row">
        <div className="filter-search">
          <Search size={16} />
          <input
            placeholder="Rechercher (accents ignorés)..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>
          <Plus size={16} /> Ajouter un bar
        </button>
        <span style={{ fontSize: '12px', color: 'var(--text-dim)', marginLeft: 'auto' }}>
          {filtered.length}{filtered.length !== bars.length ? ` / ${bars.length}` : ''} bars
        </span>
      </div>

      <div className="filters-row" style={{ marginBottom: 16 }}>
        <select
          className="filter-select"
          value={probability}
          onChange={e => setProbability(e.target.value as ProbabilityFilter)}
          aria-label="Filtrer par probabilité"
        >
          <option value="all">Probabilité : toutes</option>
          <option value="high">≥ 80 % (haute)</option>
          <option value="medium">50–79 % (moyenne)</option>
          <option value="low">&lt; 50 % (faible)</option>
          <option value="none">Aucune probabilité</option>
        </select>

        <select
          className="filter-select"
          value={channel}
          onChange={e => setChannel(e.target.value as ChannelFilter)}
          aria-label="Filtrer par chaîne TV"
        >
          <option value="all">Chaîne : toutes</option>
          <option value="Canal+">Canal+</option>
          <option value="BeIN">BeIN Sports</option>
          <option value="RMC">RMC Sport</option>
          <option value="DAZN">DAZN</option>
          <option value="Eurosport">Eurosport</option>
        </select>

        <select
          className="filter-select"
          value={visibility}
          onChange={e => setVisibility(e.target.value as VisibilityFilter)}
          aria-label="Filtrer par visibilité"
        >
          <option value="all">Visibilité : tous</option>
          <option value="visible">Visibles dans l'app</option>
          <option value="hidden">Masqués</option>
        </select>

        <select
          className="filter-select"
          value={online}
          onChange={e => setOnline(e.target.value as OnlineFilter)}
          aria-label="Filtrer par statut en ligne"
        >
          <option value="all">Statut : tous</option>
          <option value="online">En ligne</option>
          <option value="offline">Hors ligne</option>
        </select>

        <select
          className="filter-select"
          value={rating}
          onChange={e => setRating(e.target.value as RatingFilter)}
          aria-label="Filtrer par note Google"
        >
          <option value="all">Note : toutes</option>
          <option value="gte45">≥ 4,5 ★</option>
          <option value="gte40">≥ 4,0 ★</option>
          <option value="none">Sans note Google</option>
        </select>

        <select
          className="filter-select"
          value={screens}
          onChange={e => setScreens(e.target.value as ScreensFilter)}
          aria-label="Filtrer par info écrans"
        >
          <option value="all">Écrans : tous</option>
          <option value="unknown">Sans info écrans</option>
        </select>

        {(activeFilterCount > 0 || search) && (
          <button
            type="button"
            onClick={resetFilters}
            className="btn"
            style={{ gap: 6, fontSize: 12 }}
            title="Réinitialiser tous les filtres"
          >
            <X size={14} />
            Réinitialiser
            {activeFilterCount > 0 && (
              <span
                style={{
                  marginLeft: 4,
                  background: 'var(--accent)',
                  color: '#fff',
                  borderRadius: 9,
                  padding: '1px 6px',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {activeFilterCount}
              </span>
            )}
          </button>
        )}
      </div>

      <div className="card">
        {loading ? (
          <div className="skeleton-list">
            {[1,2,3,4].map(i => <div key={i} className="skeleton-row" />)}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: '44px' }}>Photo</th>
                <th>Nom</th>
                <th>Adresse</th>
                <th>Prob.</th>
                <th>Chaînes</th>
                <th>Note</th>
                <th style={{ textAlign: 'center' }}>Écrans</th>
                <th>Statut</th>
                <th style={{ textAlign: 'center' }}>Visible</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(bar => (
                <tr key={bar.id}>
                  <td>
                    {bar.image_url ? (
                      <img src={bar.image_url} alt="" style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' }} onError={e => (e.currentTarget.style.display = 'none')} />
                    ) : (
                      <div style={{ width: '36px', height: '36px', borderRadius: '6px', background: 'var(--bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '14px', opacity: 0.3 }}>?</span>
                      </div>
                    )}
                  </td>
                  <td style={{ fontWeight: 600 }}>{bar.name}</td>
                  <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{bar.address}</td>
                  <td>{probBadge(bar.probability)}</td>
                  <td style={{ fontSize: '11px', color: 'var(--text-muted)', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {bar.tv_channels || '-'}
                  </td>
                  <td>
                    {bar.google_rating ? (
                      <span style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px', color: 'var(--accent)' }}>
                        <Star size={11} fill="currentColor" /> {bar.google_rating}
                      </span>
                    ) : <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>-</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {bar.screens_count == null ? (
                      <span style={{ color: 'var(--text-dim)', fontSize: '12px', fontWeight: 600 }} title="Inconnu — à compléter">?</span>
                    ) : bar.screens_count === 0 ? (
                      <span style={{ color: 'var(--red)', fontSize: '12px', fontWeight: 600, opacity: 0.75 }} title="Bar sans écran">0</span>
                    ) : bar.screens_count >= 4 ? (
                      <span style={{ color: '#30D158', fontSize: '12px', fontWeight: 700 }}>{bar.screens_count}</span>
                    ) : (
                      <span style={{ color: 'var(--text)', fontSize: '12px', fontWeight: 600 }}>{bar.screens_count}</span>
                    )}
                  </td>
                  <td>
                    <span className={`conn-dot ${bar.is_online ? 'green' : 'red'}`} style={{ display: 'inline-block' }} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      onClick={() => handleToggleVisibility(bar)}
                      title={bar.is_visible ? 'Masquer dans l\'app' : 'Afficher dans l\'app'}
                      aria-label={bar.is_visible ? 'Masquer' : 'Afficher'}
                      style={{
                        position: 'relative',
                        width: '40px',
                        height: '22px',
                        borderRadius: '11px',
                        border: 'none',
                        background: bar.is_visible ? 'var(--green)' : 'var(--bg-hover)',
                        cursor: 'pointer',
                        transition: 'background 150ms ease',
                        padding: 0,
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '3px',
                          left: bar.is_visible ? '21px' : '3px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: '#fff',
                          transition: 'left 150ms ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                        }}
                      >
                        {bar.is_visible
                          ? <Eye size={10} color="var(--green)" />
                          : <EyeOff size={10} color="var(--text-dim)" />}
                      </span>
                    </button>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn-icon" onClick={() => setEditBar(bar)} title="Éditer"><Pencil size={15} /></button>
                    {canDelete && (
                      <button className="btn-icon danger" onClick={() => handleDeleteBar(bar)} title="Supprimer"><Trash2 size={15} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(editBar || showNew) && (
        <BarModal
          bar={editBar}
          isNew={showNew}
          onSave={handleSave}
          onDelete={canDelete ? handleDeleteBar : undefined}
          canDelete={canDelete}
          onClose={() => { setEditBar(null); setShowNew(false); }}
        />
      )}
    </div>
  );
}
